import { isAxiosError } from 'axios';

import { apiClient } from '@/shared/api/http-client';
import { createApiPath } from '@/shared/api/endpoints';
import { Pageable, PageResponse } from '@/entities/user/api/user.api';

export interface DrugDataFlags {
  baseYm: string;
  hiraMerged?: boolean;
  codeMappingDone?: boolean;
  commissionUploaded?: boolean;
  drugInfoUpdated?: boolean;
  released?: boolean;
  batchFlag?: boolean;
  mergeFlag?: boolean;
  updateFlag?: boolean;
  durFlag?: boolean;
  batchFlagUpdatedAt?: string;
  mergeFlagUpdatedAt?: string;
  updateFlagUpdatedAt?: string;
  durFlagUpdatedAt?: string;
  codeMappingFlagUpdatedAt?: string;
  manualFlag?: boolean;
  manualFlagUpdatedAt?: string;
  codeMappingFlag?: boolean;
  /** 정산수수료 동기화 완료 (레거시 commissionFlag) */
  commissionFlag?: boolean;
  commissionFlagUpdatedAt?: string;
  /** 좌측 동기화 선행조건 충족 여부 (서버 제공 시 우선) */
  leftPrerequisitesSatisfied?: boolean;
}

/** 의약품기초정보 후보 — GET /search/drug-candidates */
export interface DrugBasicInfoCandidate {
  drugCode?: string;
  drugName?: string;
  pharmaCompanyName?: string;
  maxPrice?: number | string;
  reimbursementType?: string;
  dataSourceType?: string;
  drugCodePharma?: string;
  drugCodeMfds?: string;
}

/** 제품허가상세정보 후보 */
export interface DrugMfdsCandidate {
  itemSeq?: string;
  itemName?: string;
  entpName?: string;
  packUnit?: string;
  materialName?: string;
  barCode?: string;
  atcCode?: string;
  etcOtcType?: string;
  etcOtcTypeShort?: string;
  etcOtcCode?: string;
}

/** 의약품코드매핑 후보 */
export interface DrugCodeMappingCandidate {
  pharmaCompanyName?: string;
  drugName?: string;
  drugCodeHira?: string;
  drugCode?: string;
  ingredientAmount?: string;
  barCode?: string;
  atcCode?: string;
  drugCodePharma?: string;
  drugCodeMfds?: string;
  itemSeq?: string;
  etcOtcType?: string;
  etcOtcTypeShort?: string;
  etcOtcCode?: string;
  dosageFormType?: string;
  packageType?: string;
}

export interface DrugCandidateSearchResult {
  drugBasicInfoCandidates?: DrugBasicInfoCandidate[];
  drugInfoMfdsCandidates?: DrugMfdsCandidate[];
  drugCodeMappingCandidates?: DrugCodeMappingCandidate[];
}

/** 수수료 대비 확인 필요 의약품 행 — GET /commission/validate-drug-codes items */
export interface CommissionValidationItem {
  drugCode?: string;
  drugName?: string;
  pharmaCompanyName?: string;
  pharmaCompanyId?: string | number;
  pharmaDrugCode?: string;
  drugCodePharma?: string;
  price?: number | string;
  pricePharma?: number | string;
  priceHira?: number | string;
  status?: string;
  mappingCandidates?: unknown[];
  mfdsCandidates?: unknown[];
}

export interface CommissionValidationResult {
  valid?: boolean;
  missingCount?: number;
  /** 레거시 SSOT 건수 필드 */
  totalMissingCount?: number;
  items?: CommissionValidationItem[];
  /** 구 응답 키 — normalize 시 items 로 합친다 */
  missingDrugs?: CommissionValidationItem[];
}

const asObject = (value: unknown): Record<string, unknown> | null =>
  value != null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const asArray = <T>(value: unknown): T[] =>
  Array.isArray(value) ? (value as T[]) : [];

/**
 * validate-drug-codes 응답을 레거시 모달 계약(items + totalMissingCount)으로 맞춘다.
 * 서버가 missingDrugs / missingCount 만 줘도 목록이 비지 않게 한다.
 */
export const normalizeCommissionValidationResult = (
  data: unknown,
): CommissionValidationResult => {
  const raw = asObject(data) ?? {};
  const items = asArray<CommissionValidationItem>(
    raw.items ?? raw.missingDrugs,
  );
  const countRaw = raw.totalMissingCount ?? raw.missingCount;
  const count = typeof countRaw === 'number' ? countRaw : items.length;
  return {
    valid: typeof raw.valid === 'boolean' ? raw.valid : undefined,
    items,
    missingDrugs: items,
    totalMissingCount: count,
    missingCount: count,
  };
};

/**
 * 3탭 후보 조회 응답 정규화.
 * 평탄 배열이 오면 기초정보 탭으로 넣는다 (구 핸들러 호환).
 */
export const normalizeDrugCandidateSearchResult = (
  data: unknown,
): DrugCandidateSearchResult => {
  if (Array.isArray(data)) {
    return {
      drugBasicInfoCandidates: data as DrugBasicInfoCandidate[],
      drugInfoMfdsCandidates: [],
      drugCodeMappingCandidates: [],
    };
  }
  const raw = asObject(data) ?? {};
  return {
    drugBasicInfoCandidates: asArray<DrugBasicInfoCandidate>(
      raw.drugBasicInfoCandidates,
    ),
    drugInfoMfdsCandidates: asArray<DrugMfdsCandidate>(
      raw.drugInfoMfdsCandidates,
    ),
    drugCodeMappingCandidates: asArray<DrugCodeMappingCandidate>(
      raw.drugCodeMappingCandidates,
    ),
  };
};

export interface DrugDataReleaseStatus {
  baseYm: string;
  released: boolean;
  releasedAt?: string;
  releasable?: boolean;
}

export interface DrugDataSearchRequest {
  /** 기준 연월 YYYYMM — GET /drugs 검색 (Swagger DrugSearchRequest.refYm) */
  refYm?: string;
  drugCode?: string;
  drugCodePharma?: string;
  drugCodeMfds?: string;
  drugName?: string;
  pharmaCompanyName?: string;
  reimbursementType?: string;
  /**
   * 계약 상태 필터 (예: ACTIVE).
   * 레거시 underContract=true 에 대응.
   */
  contractStatus?: string;
  dataSourceType?: string;
}

/**
 * DrugSearchRequest + Pageable → flat query.
 * Spring은 request DTO 필드를 쿼리로 펼쳐 받는다 (baseYm 아님 refYm).
 */
export const toDrugDataSearchParams = (
  request: DrugDataSearchRequest,
  pageable: Pageable,
): Record<string, string | number | boolean> => {
  const params: Record<string, string | number | boolean> = {
    page: pageable.page,
    size: pageable.size,
  };
  (
    [
      'refYm',
      'drugCode',
      'drugCodePharma',
      'drugCodeMfds',
      'drugName',
      'pharmaCompanyName',
      'reimbursementType',
      'contractStatus',
      'dataSourceType',
    ] as const
  ).forEach((key) => {
    const value = request[key];
    if (value !== undefined && value !== null && value !== '') {
      params[key] = value;
    }
  });
  return params;
};

export interface DrugManualDetail {
  drugCode?: string;
  drugCodePharma?: string;
  drugCodeMfds?: string;
  drugName?: string;
  unitQuantity?: string;
  baseUnit?: string;
  reimbursementType?: string;
  etcOtcType?: string;
  dataSourceType?: string;
  administrationRouteType?: string;
  atcCode?: string;
}

export interface DrugDataDrugItem {
  refYm: string;
  drugCode: string;
  drugCodePharma?: string;
  drugCodeMfds?: string;
  drugName: string;
  pharmaCompanyName?: string;
  reimbursementType?: string;
  dataSourceType?: string;
  registrationStatusType?: string;
  mainIngredientCode?: string;
  priceHira?: number;
}

/** 코드맵핑·DUR·병합 등 장시간 동기 작업 — 기본 10초면 브라우저가 요청을 canceled로 끊는다 */
const PIPELINE_TIMEOUT_MS = 600_000;

/**
 * 의약품 월간 수집 배치(dataCollectionJob)가 다루는 temp 테이블 5종.
 * 서버는 테이블명을 검증하지 않아 목록 밖 값은 202 후 조용히 무시되므로, 이 목록만 보낼 수 있게 한다.
 * temp_clinic_infos·temp_pharmacy_infos 는 facility-collection 배치 대상이라 제외한다.
 */
export const DATA_COLLECTION_TARGET_TABLES = [
  { value: 'temp_drug_info_hiras', label: '심평원 약가 목록' },
  { value: 'temp_drug_info_comparators', label: '대조약 목록' },
  { value: 'temp_drug_info_bioequivalences', label: '생동성 인정 품목' },
  { value: 'temp_drug_info_ingredients', label: '주성분 코드' },
  { value: 'temp_drug_info_mfds', label: '식약처 허가 상세' },
] as const;

export type DataCollectionTargetTable =
  (typeof DATA_COLLECTION_TARGET_TABLES)[number]['value'];

/**
 * 생성 단계 초기화 대상 — POST /admin/drug-data/reset `targets`.
 * 화면 단계 순서(= 서버 실행 순서)로 두고, label 은 화면 카드 제목과 맞춘다.
 * scope: 지우는 범위, lowers: 함께 미완료로 내려가는 완료 플래그(서버 DrugDataInvalidation 기준).
 */
export const DRUG_DATA_RESET_TARGETS = [
  {
    value: 'CODE_MAPPING',
    label: '의약품코드맵핑정보',
    scope: '코드 매핑 temp 테이블 전체 (월 무관)',
    lowers: '코드 매핑 · 업데이트',
  },
  {
    value: 'DUR',
    label: 'DUR점검대상비급여의약품',
    scope: 'DUR temp 테이블 전체 (월 무관)',
    lowers: 'DUR · 기초 정보 생성 · 업데이트',
  },
  {
    value: 'COMMISSION',
    label: '정산수수료 정보',
    scope: '해당 월 수수료율',
    lowers: '정산수수료 · 기초 정보 생성 · 업데이트',
  },
  {
    value: 'MERGE',
    label: '의약품 기초 정보 생성',
    scope: '해당 월 의약품 전체',
    lowers: '기초 정보 생성 · 수기 입력 · 업데이트',
  },
  {
    value: 'MANUAL',
    label: '의약품 정보 수기 입력',
    scope: '해당 월 중 수기로 새로 넣은 의약품만 (수기 수정분은 되돌리지 않음)',
    lowers: '수기 입력 · 업데이트',
  },
  {
    value: 'UPDATE',
    label: '의약품 정보 업데이트',
    scope: '해당 월 의약품 상세 정보(drug_info)',
    lowers: '업데이트',
  },
] as const;

export type DrugDataResetTarget =
  (typeof DRUG_DATA_RESET_TARGETS)[number]['value'];

export interface DrugDataResetResultItem {
  target: DrugDataResetTarget;
  tableName: string;
  deletedCount: number;
  /** 지울 행이 없으면 백업도 만들지 않아 null */
  backupTableName: string | null;
  /** 확인 필요 의약품 "생성 완료" → "생성 대기" 되돌림 건수 (MERGE·MANUAL 만) */
  revertedCommissionCount: number;
  /** 다른 대상이 같은 행을 지워 따로 처리하지 않았으면 그 대상 (MANUAL + MERGE → MERGE) */
  coveredBy: DrugDataResetTarget | null;
}

export interface DrugDataResetResponse {
  baseYm: string;
  results: DrugDataResetResultItem[];
  /** 초기화 후 완료 플래그 전체 — GET /flags 와 같은 모양 */
  flags: DrugDataFlags;
}

const DRUG_DATA_ALREADY_RELEASED_CODE = 'DRUG_DATA_ALREADY_RELEASED';
const DRUG_DATA_PROCESS_ALREADY_RUNNING_CODE =
  'DRUG_DATA_PROCESS_ALREADY_RUNNING';

const hasDrugDataErrorCode = (error: unknown, code: string): boolean => {
  if (!isAxiosError(error)) return false;
  const data = error.response?.data as { code?: unknown } | undefined;
  return data?.code === code;
};

/** 게시된 기준월은 초기화할 수 없어 409 — 게시 취소 후 초기화해야 한다 */
export const isDrugDataAlreadyReleasedError = (error: unknown): boolean =>
  hasDrugDataErrorCode(error, DRUG_DATA_ALREADY_RELEASED_CODE);

/** 다른 생성 단계·수집 배치가 실행 중이라 409 — 잠시 후 재시도 */
export const isDrugDataProcessRunningError = (error: unknown): boolean =>
  hasDrugDataErrorCode(error, DRUG_DATA_PROCESS_ALREADY_RUNNING_CODE);

export const drugDataService = {
  getFlags: async (baseYm: string): Promise<DrugDataFlags> => {
    const response = await apiClient.get(createApiPath.drugData.flags(), {
      params: { baseYm },
    });
    return response.data;
  },

  setFlags: async (body: Partial<DrugDataFlags> & { baseYm: string }) => {
    const response = await apiClient.post(
      createApiPath.drugData.setFlag(),
      body,
    );
    return response.data as DrugDataFlags;
  },

  getReleaseStatus: async (baseYm: string): Promise<DrugDataReleaseStatus> => {
    const response = await apiClient.get(createApiPath.drugData.release(), {
      params: { baseYm },
    });
    return response.data;
  },

  /** POST /admin/drug-data/release — Swagger `release`, query `baseYm` only */
  releaseDrugData: async (baseYm: string) => {
    const response = await apiClient.post(
      createApiPath.drugData.release(),
      undefined,
      { params: { baseYm } },
    );
    return response.data;
  },

  /**
   * DELETE /admin/drug-data/release — 게시(개시) 취소. query `baseYm` only.
   * 완료 플래그는 그대로라 고친 뒤 바로 다시 게시할 수 있다. 미게시 달이면 404 DRUG_DATA_NOT_RELEASED.
   */
  cancelDrugDataRelease: async (baseYm: string) => {
    const response = await apiClient.delete(createApiPath.drugData.release(), {
      params: { baseYm },
    });
    return response.data;
  },

  /**
   * POST /admin/drug-data/reset — 생성 단계 산출 테이블을 백업 후 비운다(복원 API 없음).
   * targets 를 생략하면 전체 6단계. 백업(CREATE TABLE AS)이 길어 파이프라인과 같은 타임아웃을 쓴다.
   */
  resetDrugData: async (args: {
    baseYm: string;
    targets?: DrugDataResetTarget[];
  }): Promise<DrugDataResetResponse> => {
    const response = await apiClient.post(
      createApiPath.drugData.reset(),
      {
        baseYm: args.baseYm,
        ...(args.targets?.length ? { targets: args.targets } : {}),
      },
      { timeout: PIPELINE_TIMEOUT_MS },
    );
    return response.data;
  },

  searchDrugs: async (
    request: DrugDataSearchRequest,
    pageable: Pageable,
  ): Promise<PageResponse<DrugDataDrugItem>> => {
    const response = await apiClient.get(createApiPath.drugData.drugs(), {
      params: toDrugDataSearchParams(request, pageable),
    });
    return response.data;
  },

  updateDrugInfo: async (baseYm: string) => {
    const response = await apiClient.post(
      createApiPath.drugData.updateDrugInfo(),
      undefined,
      { params: { baseYm }, timeout: PIPELINE_TIMEOUT_MS },
    );
    return response.data;
  },

  searchDrugCandidates: async (params: {
    baseYm: string;
    drugName: string;
    pharmaCompanyName?: string;
  }): Promise<DrugCandidateSearchResult> => {
    const query: Record<string, string> = {
      baseYm: params.baseYm,
      drugName: params.drugName,
    };
    const pharma = params.pharmaCompanyName?.trim();
    if (pharma) query.pharmaCompanyName = pharma;
    const response = await apiClient.get(
      createApiPath.drugData.drugCandidates(),
      { params: query },
    );
    return normalizeDrugCandidateSearchResult(response.data);
  },

  /**
   * 수수료 대비 의약품코드 검증.
   * 레거시와 같이 기본 status=ALL (생성됨/미생성 모두).
   */
  validateCommissionDrugCodes: async (
    baseYm: string,
    status: 'PENDING' | 'CREATED' | 'ALL' = 'ALL',
  ): Promise<CommissionValidationResult> => {
    const response = await apiClient.get(
      createApiPath.drugData.commissionValidate(),
      { params: { baseYm, status } },
    );
    return normalizeCommissionValidationResult(response.data);
  },

  /**
   * 데이터 수집 배치 — Swagger BATCH `runDataCollectionJob`.
   * 서버가 작업을 접수하면 바로 응답하고, 완료는 flags 폴링으로 본다.
   * baseYm·targetTables 모두 query (requestBody 없음).
   *
   * targetTables 를 생략하면 전체(5종 + 인덱스 + 배치 완료 플래그)를 돈다.
   * 일부만 보내면 배치 완료 플래그가 갱신되지 않는다(그 달 전체 수집이 이미 끝난 경우는 유지).
   * axios 기본 배열 직렬화(`targetTables[]=`)는 Spring 이 못 받으므로 쉼표로 이어 보낸다.
   */
  runDataCollectionJob: async (args: {
    baseYm: string;
    targetTables?: DataCollectionTargetTable[];
  }) => {
    const targetTables = args.targetTables?.length
      ? args.targetTables.join(',')
      : undefined;
    const response = await apiClient.post(
      createApiPath.drugData.batchDataCollection(),
      undefined,
      { params: { baseYm: args.baseYm, targetTables } },
    );
    return response.data;
  },

  mergeHiraDrug: async (baseYm: string) => {
    // Swagger: baseYm 은 query (body 아님)
    const response = await apiClient.post(
      createApiPath.drugData.hiraMerge(),
      undefined,
      { params: { baseYm }, timeout: PIPELINE_TIMEOUT_MS },
    );
    return response.data;
  },

  /** POST /admin/drug-data/commission — multipart `file` only (년월은 파일 컬럼) */
  uploadCommission: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(
      createApiPath.drugData.commissionUpload(),
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  processHiraNonBenefit: async (
    baseYm: string,
    file?: File,
    options?: { remotePath?: string; downloadFileName?: string },
  ) => {
    const formData = new FormData();
    if (file) formData.append('file', file);
    const response = await apiClient.post(
      createApiPath.drugData.hiraNonBenefit(),
      formData,
      {
        params: {
          baseYm,
          ...(options?.remotePath ? { remotePath: options.remotePath } : {}),
          ...(options?.downloadFileName
            ? { downloadFileName: options.downloadFileName }
            : {}),
        },
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: PIPELINE_TIMEOUT_MS,
      },
    );
    return response.data;
  },

  createCodeMapping: async (baseYm: string, file?: File) => {
    const formData = new FormData();
    if (file) formData.append('file', file);
    const response = await apiClient.post(
      createApiPath.drugData.codeMapping(),
      formData,
      {
        params: { baseYm },
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: PIPELINE_TIMEOUT_MS,
      },
    );
    return response.data;
  },

  findLatestDrugByPharmaCode: async (
    refYm: string,
    drugCodePharma: string,
  ): Promise<DrugManualDetail> => {
    const response = await apiClient.get(
      createApiPath.drugData.drugByPharmaCode(refYm, drugCodePharma),
    );
    return response.data;
  },

  insertDrugsManual: async (items: Record<string, unknown>[]) => {
    const response = await apiClient.post(
      createApiPath.drugData.drugs(),
      items,
    );
    return response.data;
  },

  updateDrugDataDrug: async (
    refYm: string,
    drugCode: string,
    body: Record<string, unknown>,
  ) => {
    const response = await apiClient.patch(
      `${createApiPath.drugData.drugs()}/${encodeURIComponent(refYm)}/${encodeURIComponent(drugCode)}`,
      body,
    );
    return response.data;
  },
};
